import { ArrowUpRight, ExternalLink } from "lucide-react";
import { AnimatedPage } from "../components/AnimatedPage";
import { CTASection } from "../components/CTASection";
import { PageHero } from "../components/PageHero";
import { ProjectImage } from "../components/ProjectImage";
import { Section } from "../components/Section";
import { portfolio, sampleProjects } from "../data/site";

export default function Portfolio() {
  return (
    <AnimatedPage>
      <PageHero
        eyebrow="Portfolio"
        title="Premium project patterns across growing industries"
        description="Explore the kinds of systems Lazerwebs builds for fintech, commerce, education, healthcare, logistics, agriculture, government, and enterprise teams."
      />
      <Section
        eyebrow="Industries"
        title="Digital products shaped for each market"
        description="Every industry has its own workflow, trust requirements, compliance needs, and customer expectations. The right platform accounts for all of them."
      >
        <div className="grid gap-5 md:grid-cols-2">
          {portfolio.map((project, index) => {
            return (
              <article
                key={project.industry}
                className={`glass-card overflow-hidden ${
                  index % 3 === 0 ? "md:col-span-2" : ""
                }`}
              >
                <div className="grid min-h-full gap-0 lg:grid-cols-[1.05fr_0.95fr]">
                  <ProjectImage
                    src={project.image.src}
                    alt={project.image.alt}
                    industry={project.industry}
                    icon={project.icon}
                    priority={index < 2}
                    className="aspect-[16/10] lg:aspect-auto lg:min-h-full"
                  />
                  <div className="p-8">
                    <div className="mb-8 flex items-center justify-between">
                      <p className="eyebrow">{project.industry}</p>
                      <ArrowUpRight className="h-5 w-5 text-white/40" />
                    </div>
                    <h2 className="font-display text-2xl font-semibold text-white">
                      {project.title}
                    </h2>
                    <p className="mt-4 max-w-2xl text-sm leading-7 text-white/60">
                      {project.description}
                    </p>
                    {project.sample && (
                      <a
                        href={project.sample.url}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-secondary mt-6"
                      >
                        View sample
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </Section>
      <Section
        eyebrow="Live samples"
        title="Selected work and reference platforms"
        description="These are a few public samples. More project references are available when you contact Lazerwebs."
        className="bg-white/[0.025]"
      >
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          {sampleProjects.map((sample) => (
            <article key={sample.name} className="glass-card p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-electric">
                {sample.category}
              </p>
              <h2 className="mt-4 font-display text-xl font-semibold text-white">
                {sample.name}
              </h2>
              {sample.url ? (
                <a
                  href={sample.url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-white/75 transition hover:text-white"
                >
                  Visit sample
                  <ExternalLink className="h-4 w-4" />
                </a>
              ) : (
                <p className="mt-5 text-sm font-semibold text-white/55">
                  Available on request
                </p>
              )}
            </article>
          ))}
        </div>
      </Section>
      <CTASection />
    </AnimatedPage>
  );
}
